import {
  SNAPSHOT_CONFLICT_RULE,
  type SnapshotConflict,
  type SnapshotSourceDetail,
} from "@healthos/shared";

const TIME = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });
const DATE = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short" });

/*
 * The member reads a clock, not a duration: a sync at 06:12 is "this morning",
 * one an hour ago is "an hour ago", and one last week carries its date.
 */
function seenLabel(iso: string | null): string {
  if (!iso) return "never";
  const at = new Date(iso);
  const minutes = Math.round((Date.now() - at.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const today = new Date();
  const sameDay = at.toDateString() === today.toDateString();
  if (sameDay) return at.getHours() < 12 ? `${TIME.format(at)} this morning` : TIME.format(at);
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (at.toDateString() === yesterday.toDateString()) return `Yesterday, ${TIME.format(at)}`;
  return `${DATE.format(at)}, ${TIME.format(at)}`;
}

/** The coverage read, already worded. See `snapshotCoverageProps`. */
export interface SnapshotCoverageView {
  core: { reporting: number; total: number };
  coreSay: string;
  additiveTotal: number;
  additiveSay: string | null;
}

/**
 * Core leads and gets the bar, because a gap there costs the member a reading.
 * Additive is stated as what they have, with no fraction and no gauge: it is
 * not a target, so it is not scored.
 */
function Coverage({ view }: { view: SnapshotCoverageView }) {
  const { core } = view;
  return (
    <div className="snap-cov">
      <span className="snap-cov-n">
        {core.reporting} of {core.total}
      </span>
      <span className="snap-cov-k">core signals reporting</span>
      <span className="snap-cov-bar" aria-hidden="true">
        <i style={{ width: `${core.total === 0 ? 0 : (core.reporting / core.total) * 100}%` }} />
      </span>
      <span className="snap-cov-say">{view.coreSay}</span>
      {view.additiveSay ? (
        <span className="snap-cov-add">
          <b>{view.additiveTotal} additive signals.</b> {view.additiveSay}
        </span>
      ) : null}
    </div>
  );
}

export function SnapshotSources({
  sources,
  conflicts = [],
  coverage,
  kindLabel,
  connectionsHref = "/connectors",
}: {
  sources: ReadonlyArray<SnapshotSourceDetail>;
  /** Metrics more than one device reported on the same day. */
  conflicts?: ReadonlyArray<SnapshotConflict>;
  /** How many of the readings this page can take are actually arriving. */
  coverage?: SnapshotCoverageView;
  kindLabel: (kind: string) => string;
  connectionsHref?: string;
}) {
  /*
   * The order is the member's own devices, ranked. A catalogue of providers
   * they do not own says nothing about their readings.
   */
  const order = sources
    .filter((s) => s.rank !== null)
    .slice()
    .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
    .map((s) => ({ name: s.name, relay: s.relay }));

  return (
    <div className="snap-src">
      {coverage ? <Coverage view={coverage} /> : null}
      <div className="snap-section-head">
        <h2>Where this comes from</h2>
        <a className="hint" href={connectionsHref}>
          Manage your devices
        </a>
      </div>
      {sources.length === 0 ? (
        <p className="snap-src-empty">
          Nothing is reporting yet. <a href={connectionsHref}>Connect a device</a>, or add a number
          yourself, and it appears here.
        </p>
      ) : (
        <ul className="snap-src-list">
          {sources.map((s) => (
            <li key={s.name} className={s.lastSeenAt ? "" : "is-off"}>
              <span className="snap-src-n">{s.name}</span>
              <span className="snap-src-via">{s.via ? `via ${s.via}` : ""}</span>
              <span className="snap-src-p">
                {[
                  ...s.provides.map((k) => ({ k, pending: false })),
                  ...s.pending.map((k) => ({ k, pending: true })),
                ].map(({ k, pending }, i) => (
                  <span key={k} className={pending ? "snap-src-pend" : undefined}>
                    {i > 0 ? <i className="snap-src-sep" aria-hidden="true" /> : null}
                    {kindLabel(k)}
                    {pending ? " (not yet)" : ""}
                  </span>
                ))}
                {s.provides.length === 0 && s.pending.length === 0 ? "Nothing reported yet" : null}
              </span>
              <span className="snap-src-t">{seenLabel(s.lastSeenAt)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="snap-section-head">
        <h2>Where your devices disagree</h2>
        <span className="hint">and which one we use</span>
      </div>
      <p className="snap-src-rule">{SNAPSHOT_CONFLICT_RULE}</p>
      {order.length > 1 ? (
        <ol className="snap-pref-order" aria-label="Which of your devices we read first">
          {order.map((s) => (
            <li key={s.name} className={s.relay ? "is-relay" : undefined}>
              {s.name}
            </li>
          ))}
        </ol>
      ) : null}
      {conflicts.length === 0 ? (
        <p className="snap-src-empty">
          Nothing has disagreed this week. Two devices reporting the same reading on the same day
          appear here, with both numbers.
        </p>
      ) : (
        <ul className="snap-conf-list">
          {conflicts.map((c) => (
            <li key={c.metric}>
              <span className="snap-conf-m">{c.metric}</span>
              <span className="snap-conf-w">
                <b>{c.used.display}</b> {c.used.source}
              </span>
              <span className="snap-conf-l">
                <s>
                  {c.other.display} {c.other.source}
                </s>
              </span>
              <span className="snap-conf-y">{c.why ? `${c.why} ` : ""}Both are kept.</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
